import React, { useMemo } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, Typography, Breadcrumbs, Divider, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link } from 'react-router-dom';
import { Save, Visibility, InfoOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { ModalViewPdfGlobal } from '../../../components/modals/viwerPdf/ModalViewPdfGlobal';
import { InfoModal } from '../../../components/generics/InfoModal';
import * as Yup from 'yup';

import { useResponseHandler } from '../../../hooks/useResponseHandler';
import InputField from '../../../components/formFields/InputField';
import AutocompleteField from '../../../components/formFields/autocompleteField';
import { createKnowledgeBase, getDataFormDropDownStore } from 'store/knowledge-bases/thunks';
import { UploadButtonField } from '../../../components/formFields/uploadButtonField';
import { SnackbarProgress } from '../../../components';
import { baseConocimientoFormModel, formInitialValues, createValidationSchema } from './formik';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 50,
  padding: 24,
  borderRadius: 16,
  boxShadow:
    '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)',
}));

const { formField } = baseConocimientoFormModel;

export const CreateBaseConocimiento = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [uploadPercentage, setUploadPercentage] = React.useState(0);
  const { handleResponse, ResponseComponents } = useResponseHandler() as any;
  const [activeModal, setActiveModal] = React.useState<string | null>(null);

  const { knowledge_bases_form_dropdown } = useSelector((state: any) => state.knowledgeBases);
  const providers = knowledge_bases_form_dropdown?.providers || [];
  const embeddingModels = knowledge_bases_form_dropdown?.services || [];

  const [showPdfDetail, setShowPdfDetail] = React.useState(false);
  const [urlRutPdf, setUrlRutPdf] = React.useState<string | null>(null);
  const [typeUrl, setTypeUrl] = React.useState<string | null>(null);

  const handleOpenPdf = (url: string, urlTypeUrl: string) => {
    setUrlRutPdf(url);
    setTypeUrl(urlTypeUrl);
    setShowPdfDetail(true);
  };

  React.useEffect(() => {
    getDataForm();
  }, []);

  const getDataForm = async () => {
    await dispatch(getDataFormDropDownStore());
  };

  const handleSubmit = async (values: any, actions: any) => {
    const formData = new FormData();
    formData.append('name', values.name);
    if (values.description) formData.append('description', values.description);
    if (values.embeddingProvider)
      formData.append('embeddingProvider', values.embeddingProvider.value);
    if (values.embeddingModel) formData.append('embeddingModel', values.embeddingModel.value);
    if (values.file) formData.append('file', values.file);

    const response = await dispatch(createKnowledgeBase(formData, setUploadPercentage));
    const success = await handleResponse(response, 'crear base de conocimiento', true);

    // Si queremos esconder la barra luego de subir
    setTimeout(() => setUploadPercentage(0), 1000);

    if (success) {
      setTimeout(() => {
        navigate('/inteligencia-artificial/base-conocimiento');
      }, 1500);
    }
    actions.setSubmitting(false);
  };

  const getModalContent = () => {
    switch (activeModal) {
      case 'name':
        return {
          title: 'Nombre',
          description: 'El nombre identificador de esta base de conocimiento.',
          infoText: 'Ejemplo: "Reglamento del Hotel" o "Políticas de Cancelación".',
        };
      case 'description':
        return {
          title: 'Descripción',
          description:
            'Una breve explicación del contenido o propósito de esta base de conocimiento.',
          infoText: 'Ejemplo: "Contiene las normas internas y horarios para los empleados."',
        };
      case 'embeddingProvider':
        return {
          title: 'Proveedor de Embedding',
          description:
            'La empresa que provee el modelo matemático que convertirá tus textos en vectores.',
          infoText:
            'Ejemplo: Google (Gemini) u OpenAI. Esto define qué infraestructura procesará tus documentos.',
        };
      case 'embeddingModel':
        return {
          title: 'Modelo de Embedding',
          description:
            'El modelo de inteligencia artificial específico utilizado para procesar los textos.',
          infoText:
            'Ejemplo: gemini-1.5-pro o text-embedding-3-small. Diferentes modelos tienen diferentes niveles de comprensión y costos.',
        };
      default:
        return { title: '', description: '', infoText: '' };
    }
  };

  const modalContent = getModalContent();

  const renderFieldWithInfo = (field: React.ReactNode, modalKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>{field}</Box>
      <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
        <InfoOutlined />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <SnackbarProgress
        open={uploadPercentage > 0 && uploadPercentage < 100}
        percentage={uploadPercentage}
        label="Subiendo documento de conocimiento..."
      />

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>
        Crear Base de Conocimiento
      </Typography>
      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
        <Typography color="textPrimary" sx={{ fontWeight: 700 }}>
          IA
        </Typography>
        <Link
          style={{ color: 'inherit', textDecoration: 'none' }}
          to="/inteligencia-artificial/base-conocimiento"
        >
          Bases de Conocimiento
        </Link>
        <Typography color="textPrimary">Crear</Typography>
      </Breadcrumbs>
      <Divider sx={{ marginBottom: 3 }} />

      <Formik
        initialValues={formInitialValues}
        validationSchema={createValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values }) => {
          const provider = values.embeddingProvider as { value: string; label: string } | null;
          const filteredModels = provider
            ? embeddingModels
                .filter((m: any) => m.provider === provider.value)
                .map((m: any) => ({ value: m.code, label: m.name }))
            : [];

          return (
            <StyledCard>
              <Form autoComplete="nope" id={baseConocimientoFormModel.formId}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
                    Información General
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {renderFieldWithInfo(
                        <InputField
                          name={formField.name.name}
                          label={formField.name.label}
                          fullWidth
                          variant="outlined"
                        />,
                        'name'
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {renderFieldWithInfo(
                        <InputField
                          name={formField.description.name}
                          label={formField.description.label}
                          fullWidth
                          variant="outlined"
                        />,
                        'description'
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {renderFieldWithInfo(
                        <AutocompleteField
                          name={formField.embeddingProvider.name}
                          label={formField.embeddingProvider.label}
                          options={providers}
                          onChange={(event: any, newValue: any) => {
                            setFieldValue(formField.embeddingModel.name, null);
                          }}
                          fullWidth
                        />,
                        'embeddingProvider'
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {renderFieldWithInfo(
                        <AutocompleteField
                          name={formField.embeddingModel.name}
                          label={formField.embeddingModel.label}
                          options={filteredModels}
                          fullWidth
                          disabled={!values.embeddingProvider}
                        />,
                        'embeddingModel'
                      )}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {formField.file.label}
                      </Typography>
                      <UploadButtonField
                        name={formField.file.name}
                        text={values.file ? (values.file as any).name : 'Subir Archivo (PDF)'}
                        accept=".pdf"
                        onChange={(e: any) => {
                          if (e.target.files && e.target.files.length > 0) {
                            setFieldValue('file', e.target.files[0]);
                          }
                        }}
                      />
                      {values.file && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            Archivo seleccionado: {(values.file as any).name}
                          </Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              const fileUrl = URL.createObjectURL(values.file as any);
                              handleOpenPdf(fileUrl, 'local');
                            }}
                            title="Ver documento"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 4 }} />

                <ModalViewPdfGlobal
                  open={showPdfDetail}
                  typeUrl={typeUrl}
                  onClose={() => setShowPdfDetail(false)}
                  pdfUrl={urlRutPdf}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/inteligencia-artificial/base-conocimiento')}
                    disabled={isSubmitting}
                  >
                    Atrás
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    startIcon={<Save />}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                  </Button>
                </Box>
              </Form>
            </StyledCard>
          );
        }}
      </Formik>

      <InfoModal
        open={!!activeModal}
        setOpen={(isOpen: boolean) => {
          if (!isOpen) setActiveModal(null);
        }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </Box>
  );
};
